import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowUpRight } from 'lucide-react';

const Footer = ({ onOpenContactForm }) => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#09090B] text-zinc-400" data-testid="site-footer">
      <div className="container mx-auto px-6 lg:px-16 pt-20 pb-10">
        {/* Top CTA strip */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-16 pb-16 border-b border-zinc-800">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>
              Ready to protect your brand?
            </h3>
            <p className="text-zinc-500 text-sm mt-1">Start your trademark registration today. Plans from $45.</p>
          </div>
          <button
            onClick={onOpenContactForm}
            className="btn-lift flex items-center gap-2 bg-[#002FA7] hover:bg-[#001D66] text-white font-semibold px-6 py-3 rounded-md text-sm transition-all"
            data-testid="footer-cta-button"
          >
            Get Started <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div>
            <span className="text-xl font-black tracking-tight text-white block mb-4" style={{fontFamily:'Outfit,sans-serif'}}>
              Trademark<span className="text-[#002FA7]">My</span>LogoName
            </span>
            <p className="text-sm leading-relaxed text-zinc-500">
              Professional trademark registration, copyright protection, and Amazon Brand Registry services for businesses across the United States.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-zinc-300 text-xs tracking-[0.15em] uppercase mb-5" style={{fontFamily:'Outfit,sans-serif'}}>Navigate</h4>
            <ul className="space-y-3">
              {['Home', 'Services', 'Pricing', 'Testimonials', 'FAQ'].map((label) => (
                <li key={label}>
                  <button onClick={() => scrollToSection(label.toLowerCase())} className="link-underline text-sm text-zinc-500 hover:text-white transition-colors" data-testid={`footer-link-${label.toLowerCase()}`}>{label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-zinc-300 text-xs tracking-[0.15em] uppercase mb-5" style={{fontFamily:'Outfit,sans-serif'}}>Services</h4>
            <ul className="space-y-3">
              {[
                { label: 'Trademark Registration', path: '/services/trademark-registration' },
                { label: 'Copyright Registration', path: '/services/copyright-registration' },
                { label: 'Company Formation', path: '/services/company-formation' },
                { label: 'Trademark Search', path: '/services/trademark-search' },
                { label: 'Amazon Brand Registry', path: '/services/amazon-brand-registry' },
              ].map((s) => (
                <li key={s.path}>
                  <Link to={s.path} className="link-underline text-sm text-zinc-500 hover:text-white transition-colors" data-testid={`footer-service-${s.path.split('/').pop()}`}>{s.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-zinc-300 text-xs tracking-[0.15em] uppercase mb-5" style={{fontFamily:'Outfit,sans-serif'}}>Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#002FA7] flex-shrink-0 mt-0.5" />
                <a href="tel:+18005551234" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-phone">(800) 555-1234</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#002FA7] flex-shrink-0 mt-0.5" />
                <a href="mailto:info@trademarkmylogoname.com" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-email">info@trademarkmylogoname.com</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#002FA7] flex-shrink-0 mt-0.5" />
                <span className="text-sm" data-testid="footer-address">515 Flower St<br/>Los Angeles, CA 90071</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-zinc-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-600 text-xs">&copy; {currentYear} Trademark My Logo Name. All rights reserved.</p>
          <div className="flex gap-6 text-xs">
            <Link to="/privacy-policy" className="text-zinc-600 hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms-conditions" className="text-zinc-600 hover:text-white transition-colors" data-testid="footer-terms-link">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
